import type { ApiKeysGuideMessages } from '../guideTypes';

export const guideEs: ApiKeysGuideMessages = {
  documentTitle: 'Claves API y Atajos — My Budget',
  backHome: 'Inicio',
  backSettings: 'Ajustes',
  navAria: 'Guía',

  heroLabel: 'Automatización',
  heroTitle: 'Claves API y Atajos de iOS',
  heroLead:
    'Usa claves API con permisos acotados para registrar compras desde Atajos — sin iniciar sesión completa en la web desde la automatización.',

  tocLabel: 'En esta página',
  tocOverview: 'Qué son las claves API',
  tocCreate: 'Crear una clave',
  tocShortcuts: 'Configurar Atajos',
  tocFaq: 'Preguntas frecuentes',

  overviewTitle: 'Qué son las claves API',
  overviewBody:
    'Una clave API es un secreto que creas en Ajustes. Las automatizaciones la envían en la cabecera X-API-KEY para que Balance acepte compras y, si lo permites, lea resúmenes de presupuestos activos en tu nombre.',
  overviewNotLogin:
    'Las claves API son para Atajos y scripts — no sustituyen el inicio de sesión Cognito de la app web.',
  permTitle: 'Permisos',
  permReadCode: 'budget:read',
  permReadBody: 'Permite leer resúmenes de presupuestos activos (por ejemplo GET /api/v1/budgets/summaries/active).',
  permWriteCode: 'transactions:write',
  permWriteBody:
    'Necesario para registrar compras con POST /api/v1/spending. Actívalo para cualquier automatización de compras.',
  securityTitle: 'Consejos de seguridad',
  securityTip1: 'Nunca compartas la clave en chats, correos o capturas.',
  securityTip2: 'Cópiala al crearla — el valor completo solo se muestra una vez.',
  securityTip3: 'Si pudo filtrarse, bórrala en Ajustes y crea una nueva.',
  securityTip4: 'Prefiere una clave por dispositivo o automatización para revocar con precisión.',

  createTitle: 'Crear una clave en Ajustes',
  createLead: 'Debes estar conectado en la app web. Las claves se gestionan en Ajustes → Claves API.',
  createStep1Title: 'Abre Ajustes',
  createStep1Body: 'Inicia sesión y abre Ajustes desde la navegación inferior (o de escritorio).',
  createStep2Title: 'Pon un nombre',
  createStep2Body: 'Usa un nombre claro como “Atajos iPhone” para recordar qué desbloquea.',
  createStep3Title: 'Elige permisos',
  createStep3Body:
    'Para registrar compras, activa transactions:write. Añade budget:read solo si el Atajo también lee resúmenes de presupuestos.',
  createStep4Title: 'Copia de inmediato',
  createStep4Body: 'Guarda la clave en un sitio seguro antes de cerrar el aviso de revelación única.',
  createFigureCaption:
    'UI real de Ajustes → Claves API: crea una clave con transactions:write y copia el secreto único del aviso ámbar.',
  openSettingsCta: 'Abrir Ajustes → Claves API',

  shortcutsTitle: 'Configurar Atajos de iOS',
  shortcutsLead:
    'Las compras con tarjeta se pueden registrar con una automatización personal de Atajos que llama a la API de spending. Configúrala una vez; luego cada pago compatible puede enviarse en segundo plano.',
  shortcutsEndpointLabel: 'Endpoint',
  shortcutsFieldsTitle: 'Campos del cuerpo JSON',
  fieldCommerce: 'commerceName',
  fieldCommerceHint: 'Nombre de la tienda o comercio (obligatorio).',
  fieldAmount: 'amount',
  fieldAmountHint: 'Número o texto como 12.50, 12,50 o €12,50. Si hay moneda, se interpreta cuando es posible.',
  fieldDate: 'occurredDate',
  fieldDateHint: 'Fecha de la compra en YYYY-MM-DD (obligatorio).',
  responseAssigned:
    '201 + status ASSIGNED — existía un presupuesto raíz que cubría esa fecha; se creó una transacción normal.',
  responseUnassigned:
    '202 + status UNASSIGNED — no había presupuesto que cubriera la fecha; la compra queda pendiente de asignar en la web.',

  shortcutStep1Title: 'Crea una automatización Wallet / Transaction',
  shortcutStep1Body:
    'Abre Atajos → Automatización → +. Elige Transaction (iOS 17–18) o Wallet (iOS más reciente). Elige Payment, selecciona las tarjetas, activa Ejecutar de inmediato y desactiva Preguntar antes de ejecutar.',
  shortcutStep1Caption:
    'Automatización Transaction “When I tap”: elige Payment y las tarjetas de Wallet (UI real de Atajos).',
  shortcutStep2Title: 'Crea la lista de acciones',
  shortcutStep2Body:
    'Pulsa Crear atajo nuevo. Busca Obtener el contenido de la URL. Evita campos en “Preguntar cada vez” si quieres registro silencioso.',
  shortcutStep3Title: 'Configura POST, URL y cabeceras',
  shortcutStep3Body:
    'Método: POST. URL: tu base de API + /v1/spending (ejemplo de producción abajo). Cabeceras: Content-Type = application/json y X-API-KEY = tu secreto (no Authorization / Bearer).',
  shortcutStep3Caption:
    'Obtener el contenido de la URL con método POST y cabeceras — añade X-API-KEY con tu secreto de My Budget (ilustración de una captura pública de Atajos).',
  shortcutStep4Title: 'Envía el cuerpo JSON',
  shortcutStep4Body:
    'Cuerpo: JSON. Mapea commerceName, amount y occurredDate desde Entrada del atajo (Comercio, Importe, Fecha) — no Preguntar cada vez — para que Wallet rellene solo.',
  shortcutStep4Caption:
    'Usa las propiedades de Entrada del atajo Comercio / Importe / Fecha para los campos JSON — evita Preguntar cada vez.',
  shortcutStep5Title: 'Guarda y prueba',
  shortcutStep5Body:
    'Guarda la automatización. Haz una compra pequeña con Apple Pay o un atajo de prueba con JSON de ejemplo. Comprueba que el gasto aparece en My Budget, o como sin asignar si ningún presupuesto cubre esa fecha.',
  shortcutStep6Title: 'Conecta bien los campos del pago',
  shortcutStep6Body:
    'Si Atajos pide comercio o importe tras pagar, los campos no están mapeados a Entrada del atajo. Edita cada campo → Seleccionar variable → Entrada del atajo → Comercio / Importe / Fecha.',
  exampleJsonLabel: 'Ejemplo de cuerpo',

  faqTitle: 'Preguntas frecuentes',
  faq1Q: '¿Dónde creo una clave API?',
  faq1A:
    'Inicia sesión en la web → Ajustes → Claves API. Crea una clave con nombre, copia el secreto una vez y pégalo en la cabecera del Atajo.',
  faq2Q: '¿Qué permisos necesito?',
  faq2A:
    'transactions:write para POST /api/v1/spending. budget:read solo si también llamas a resúmenes de presupuestos activos. Concede el mínimo necesario.',
  faq3Q: '¿Por qué recibo HTTP 401?',
  faq3A:
    'Suele faltar la cabecera X-API-KEY, estar mal escrita o la clave fue eliminada. El nombre debe ser exactamente X-API-KEY.',
  faq4Q: '¿Por qué recibo HTTP 403?',
  faq4A:
    'La clave autenticó pero no tiene el permiso del endpoint (por ejemplo spending sin transactions:write). Edita permisos en Ajustes o crea una clave nueva con los scopes correctos.',
  faq5Q: '¿Necesito HTTPS?',
  faq5A:
    'Sí en producción. Usa https://budget.florez.xyz/api/v1/spending (o tu API desplegada con HTTPS). En iOS, Atajos suele exigir HTTPS para URLs remotas fuera del desarrollo local.',
  faq6Q: '¿Y si no hay presupuesto que cubra la fecha?',
  faq6A:
    'La API responde 202 con status UNASSIGNED y guarda la compra. En la web puedes asignarla o descartarla — no afecta totales hasta asignarla.',
  faq7Q: '¿Una clave API me inicia sesión en la web?',
  faq7A:
    'No. Solo desbloquea endpoints de automatización anotados. El uso diario de My Budget sigue con Cognito.',
  faq8Q: '¿Cómo deben verse amount y date?',
  faq8A:
    'amount acepta textos flexibles (12.50, 12,50, €12,50). occurredDate debe ser fecha ISO YYYY-MM-DD (por ejemplo 2026-07-06).',

  footerNote: 'Documentación de automatización de My Budget — claves acotadas para Atajos, no login completo.',
};
